import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { stripePromise } from '../lib/stripe';
import { supabase } from '../lib/supabase';

interface PaymentModalProps {
  open: boolean;
  projectId: string;
  milestoneId?: string;
  amountCents: number;
  description?: string;
  onClose: () => void;
  onSuccess: () => void;
}

function CheckoutForm({
  onSuccess,
  onClose,
}: {
  onSuccess: () => void;
  onClose: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message ?? 'Payment failed');
      setProcessing(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <PaymentElement />
        {error && <Alert severity="error">{error}</Alert>}
        <DialogActions sx={{ px: 0, pb: 0 }}>
          <Button onClick={onClose} disabled={processing}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!stripe || processing}
            startIcon={processing ? <CircularProgress size={16} /> : undefined}
          >
            {processing ? 'Processing…' : 'Pay now'}
          </Button>
        </DialogActions>
      </Stack>
    </form>
  );
}

export function PaymentModal({
  open,
  projectId,
  milestoneId,
  amountCents,
  description,
  onClose,
  onSuccess,
}: PaymentModalProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchClientSecret = useCallback(async () => {
    setFetchError(null);
    setClientSecret(null);

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setFetchError('Not authenticated');
      return;
    }

    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const res = await fetch(`${supabaseUrl}/functions/v1/create-payment-intent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY ?? '',
      },
      body: JSON.stringify({ projectId, milestoneId, amountCents, description }),
    });

    const json = await res.json();
    if (!res.ok) {
      setFetchError(json.error ?? 'Failed to initialise payment');
      return;
    }

    setClientSecret(json.clientSecret);
  }, [projectId, milestoneId, amountCents, description]);

  useEffect(() => {
    if (open) {
      fetchClientSecret();
    }
  }, [open, fetchClientSecret]);

  const dollars = (amountCents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Pay {dollars}
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {description}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {fetchError && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error">{fetchError}</Alert>
          </Box>
        )}
        {!fetchError && !clientSecret && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CheckoutForm onSuccess={onSuccess} onClose={onClose} />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PaymentModal;
